import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { SquareLogo, ShopifyLogo, EbayLogo } from '../components/BrandLogos';

const integrations = {
  square: {
    name: 'Square POS',
    Logo: SquareLogo,
    description: 'Seamlessly connect XLabelSync with your Square POS system to manage products across your retail locations.',
    features: [
      'One-click OAuth connect — no API keys needed',
      'Push products directly to Square with name, price, barcode, and images',
      'Auto-fetch your Square locations for inventory tracking',
      'Sync categories from your Square catalog',
      'Manage inventory quantities per location',
      'All product data including images uploaded automatically',
    ],
    howItWorks: [
      'Go to Integrations page and click "Connect Square"',
      'Sign in to your Square account and authorize XLabelSync',
      'Your locations are automatically fetched',
      'Start pushing products from the Product Form → Square tab',
    ],
  },
  shopify: {
    name: 'Shopify',
    Logo: ShopifyLogo,
    description: 'Connect your Shopify store to XLabelSync and list products with full details including shipping profiles, inventory, and images.',
    features: [
      'One-click OAuth connect — just enter your store name',
      'Push products with title, description, price, compare-at price, and cost',
      'Auto-assign shipping profiles from your Shopify store',
      'Upload product images automatically',
      'Set weight, vendor, SKU, and product type',
      'Track inventory with location-based quantities',
      'Add products to Shopify collections automatically',
    ],
    howItWorks: [
      'Go to Integrations page and enter your Shopify store name',
      'Click "Connect Shopify" and authorize on Shopify\'s site',
      'Your shipping profiles and locations are auto-loaded',
      'Push products from the Product Form → Shopify tab',
    ],
  },
  ebay: {
    name: 'eBay',
    Logo: EbayLogo,
    description: 'List products directly to eBay from XLabelSync with full listing details, market research, and automatic token refresh.',
    features: [
      'One-click OAuth connect with forced login for multi-account support',
      'Create eBay listings with title, description, price, condition, and images',
      'Built-in eBay Market Research — see average selling prices before listing',
      'Category search with full path display (leaf categories only)',
      'Set shipping method, cost, weight, and package dimensions',
      'Configure return policy (accepted/not, 14/30/60 days)',
      'Dynamic Item Specifics — add any name/value pairs eBay requires',
      'Pre-filled fields: Type, Brand, Color, Model, MPN, Material, Size',
      'Auto-refresh tokens — customers never see expiration errors',
      'Connect/Disconnect/Reconnect to switch between eBay accounts',
    ],
    howItWorks: [
      'Go to Integrations page and click "Connect eBay"',
      'Sign in to your eBay seller account and authorize',
      'Go to Product Form → eBay tab to start listing',
      'Search for a category, fill in details, check sold prices, and push',
    ],
  },
  // Note: A "stripe" integration page was previously defined here. It was
  // removed to avoid any appearance of off-platform billing for Shopify App
  // Store reviewers (App Store policy 1.2.1). Direct customers still see
  // Stripe Checkout when they upgrade from the Dashboard, but we don't
  // advertise it publicly anymore.
};

export default function IntegrationDetail() {
  const { slug } = useParams();
  const integration = integrations[slug];

  if (!integration) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Integration not found</h1>
          <Link to="/" className="text-[#0052FF] hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const Logo = integration.Logo;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="flex items-center justify-between px-6 py-4 max-w-4xl mx-auto">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg overflow-hidden"><img src="/logo.png" alt="XLabelSync" className="w-8 h-8 object-cover" /></div>
          <span className="font-bold text-lg text-[#0F172A]">XLabelSync</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0052FF]"><ArrowLeft className="w-4 h-4" /> Back</Link>
      </nav>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <Logo className="w-14 h-14" />
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A]">{integration.name}</h1>
            <p className="text-sm text-slate-500">XLabelSync Integration</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm mb-8">
          <p className="text-lg text-slate-600 leading-relaxed">{integration.description}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <h2 className="text-xl font-bold text-[#0F172A] mb-5">What XLabelSync Does</h2>
            <ul className="space-y-3">
              {integration.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-600">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <h2 className="text-xl font-bold text-[#0F172A] mb-5">How It Works</h2>
            <ol className="space-y-4">
              {integration.howItWorks.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#0052FF] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <span className="text-sm text-slate-600 pt-1">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-8">
              <Link to="/login" className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-6 py-3 text-sm transition-colors">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
